import type { Block } from "./blocknote";

/**
 * Depth-first lookup of a block by id, descending into blocks nested under
 * list items. `content.find` only sees the top level, which silently misses
 * anything the user indented.
 */
export function findBlock<T extends { id: string; children: T[] }>(
  blocks: T[],
  id: string,
): T | undefined {
  for (const block of blocks) {
    if (block.id === id) {
      return block;
    }
    const nested = findBlock(block.children, id);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

/**
 * Return a copy of `blocks` with the checklist item `id` set to `checked`,
 * descending into nested blocks. Functional rather than mutating because
 * BlockNote block props are read-only.
 */
export function setChecklistItemChecked(blocks: Block[], id: string, checked: boolean): Block[] {
  return blocks.map((block) => {
    if (block.id === id) {
      return block.type === "checkListItem"
        ? { ...block, props: { ...block.props, checked } }
        : block;
    }
    return { ...block, children: setChecklistItemChecked(block.children, id, checked) };
  });
}

/**
 * Flatten a BlockNote document to a depth-first list, so scans over block
 * types see items the user indented under a list item as well as top-level
 * ones.
 */
export function flattenBlocks<T extends { children: T[] }>(blocks: T[]): T[] {
  return blocks.flatMap((block) => [block, ...flattenBlocks(block.children)]);
}
